import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SchemaInjector } from '../components/SchemaInjector';
import { 
  CheckCircle,
  Shield,
  Wrench,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Camera,
  Zap
} from 'lucide-react';

const SewerSnakeAugerPricing = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  // Unified Schema for the entire page
  const completePageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://colorado-plumbing.com/sewer-snake-auger-pricing#webpage",
        "url": "https://colorado-plumbing.com/sewer-snake-auger-pricing",
        "name": "Denver Drain Snake Cost: Toilet $225, Mainline $295 | Colorado First Plumbing",
        "description": "See exact, flat-rate prices for drain snake services in Denver. Toilet/Branch Line: $225. Kitchen Sink: $275. Mainline: $295. 360 Google Reviews. No hidden fees.",
        "isPartOf": { "@id": "https://colorado-plumbing.com/#website" },
        "breadcrumb": { "@id": "https://colorado-plumbing.com/sewer-snake-auger-pricing#breadcrumb" }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://colorado-plumbing.com/sewer-snake-auger-pricing#breadcrumb",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://colorado-plumbing.com/" },
          { "@type": "ListItem", "position": 2, "name": "Drain Snake Pricing", "item": "https://colorado-plumbing.com/sewer-snake-auger-pricing" }
        ]
      },
      {
        "@type": "Plumber",
        "@id": "https://colorado-plumbing.com/",
        "name": "Colorado First Plumbing and Sewer LLC",
        "image": "https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Colorado-First-Plumbing-Logo-Transparent.png",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "5108 Sydney Ave", "addressLocality": "Littleton", "addressRegion": "CO", "postalCode": "80130", "addressCountry": "US"
        },
        "url": "https://colorado-plumbing.com/sewer-snake-auger-pricing",
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" },
        "areaServed": [
          { "@type": "City", "name": "Denver" },
          { "@type": "AdministrativeArea", "name": "Denver Metro" }
        ]
      },
      {
        "@type": "Product",
        "name": "Toilet & Branch Line Snake Service",
        "description": "Our $225 flat-rate service covers professional snaking of toilets, bathroom sinks, showers, and bathtub drains to clear common clogs and restore drainage.",
        "sku": "SNAKE-BRANCH-225",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/sewer-snake-auger-pricing#snake-auger-pricing-details", "priceCurrency": "USD", "price": "225", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product",
        "name": "Kitchen & Utility Line Snake Service",
        "description": "Our $275 flat-rate service is for tougher clogs in kitchen sinks, laundry lines, and utility sinks, using professional equipment to cut through grease and food blockages.",
        "sku": "SNAKE-KITCHEN-275",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/sewer-snake-auger-pricing#snake-auger-pricing-details", "priceCurrency": "USD", "price": "275", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product",
        "name": "Mainline Snake via Cleanout Access",
        "description": "For main sewer line blockages, our $295 flat-rate service restores drainage for the entire home. This price applies when accessing the line via a standard exterior or basement cleanout.",
        "sku": "SNAKE-MAIN-295",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/sewer-snake-auger-pricing#snake-auger-pricing-details", "priceCurrency": "USD", "price": "295", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product",
        "name": "Mainline Snake via Pulled Toilet",
        "description": "When no cleanout is available, we can access the mainline by removing a toilet. Our $695 flat-rate price includes the mainline snaking service plus the labor to pull and reset the toilet with a new wax ring.",
        "sku": "SNAKE-MAIN-PULL-695",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/sewer-snake-auger-pricing#snake-auger-pricing-details", "priceCurrency": "USD", "price": "695", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How much does it cost to snake a drain in Denver?",
            "acceptedAnswer": { "@type": "Answer", "text": "Colorado First Plumbing offers exact, flat-rate pricing for drain snaking in Denver. A toilet or branch line (bathroom sink, shower) costs $225. A kitchen or laundry line is $275. A mainline snake via a cleanout is $295. We show all our prices online." }
          },
          {
            "@type": "Question",
            "name": "What is the difference between snaking and hydro jetting?",
            "acceptedAnswer": { "@type": "Answer", "text": "Snaking (or using an auger) is a mechanical process that breaks up a clog to restore flow. It's great for immediate blockages. Hydro jetting is a high-pressure water cleaning service that scours the entire pipe wall to remove grease, scale, and roots for a more thorough, long-lasting clean." }
          },
          {
            "@type": "Question",
            "name": "Will a drain snake remove tree roots?",
            "acceptedAnswer": { "@type": "Answer", "text": "A standard drain snake is not designed to effectively remove tree roots. While it may punch a temporary hole through them, the roots will quickly grow back. For proper root removal, specialized root-cutting equipment or hydro jetting is required." }
          },
          {
            "@type": "Question",
            "name": "What is the price for a mainline snake if I don't have a cleanout?",
            "acceptedAnswer": { "@type": "Answer", "text": "If there is no accessible cleanout for the main sewer line, we can access the pipe by removing a toilet. Our flat-rate price for this service is $695. This includes the cost of the mainline snaking plus the labor to pull and professionally reset the toilet with a new seal." }
          }
        ]
      }
    ]
  };

  // Real Snake/Auger Sewer Cleaning Projects
  const snakeProjects = [
    {
      id: 1,
      image: '/colorado-plumbing-uploads/existing-projects/sewer-repair-experts.png',
      location: 'Denver Metro, CO',
      type: 'Professional Sewer Snake Equipment',
      description: 'Colorado First Plumbing sewer cleaning specialists with Milwaukee professional snake equipment',
      features: ['Milwaukee Professional Equipment', 'Expert Technicians', 'Complete Service Fleet']
    },
    {
      id: 2,
      image: '/colorado-plumbing-uploads/locations/denver/camera-sewer-inspection-denver.JPEG',
      location: 'Denver, CO',
      type: 'Sewer Camera Inspection & Snake Service',
      description: 'Professional sewer camera inspection combined with expert snake clearing services',
      features: ['Video Camera Inspection', 'Professional Snake Clearing', 'Complete Diagnosis']
    },
    {
      id: 3,
      image: '/colorado-plumbing-uploads/locations/denver/clean-out-install-sewer-cleaning-denver.JPEG',
      location: 'Denver, CO',
      type: 'Cleanout Access for Professional Snaking',
      description: 'Installing proper cleanout access for efficient mainline sewer snake services',
      features: ['Professional Cleanout Installation', 'Easy Future Access', 'Code Compliant Work']
    },
    {
      id: 4,
      image: '/colorado-plumbing-uploads/locations/denver/sewer-spot-repair-denver.jpeg',
      location: 'Denver, CO',
      type: 'Mainline Sewer Snake & Spot Repair',
      description: 'Expert mainline sewer snaking followed by professional spot repair services',
      features: ['Advanced Snake Equipment', 'Spot Repair Expertise', 'Complete Restoration']
    }
  ];
  useEffect(() => {
    // Auto-advance hero gallery every 5 seconds
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % snakeProjects.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [snakeProjects.length]);
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % snakeProjects.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + snakeProjects.length) % snakeProjects.length);
  };

  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <div className="min-h-screen bg-neutral-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-deep to-authority-navy">
        <div className="container mx-auto px-4 text-center relative z-10 py-16 md:py-24">
          <div className="inline-block px-6 py-3 mb-8 rounded-full bg-success-primary/90 text-white font-bold text-lg">
            🔧 Professional Snake Equipment • Expert Technicians • Transparent Pricing
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-neutral-white leading-tight">
            Denver's Honest Drain Snake Pricing
            <span className="block text-2xl md:text-3xl text-trust-light font-semibold mt-2">
              See Exact Costs for Toilet, Kitchen & Mainline Snaking
            </span>
          </h1>
          
          <p className="text-lg text-trust-light/90 mb-8">
            Master Plumber Owned • 360 Google Reviews • Professional Milwaukee Equipment
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            
            <a 
              href="#snake-auger-pricing-details" 
              className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300"
            >
              💰 See All Pricing Below
            </a>
          </div>
        </div>
      </section>
      {/* Proof of Work Gallery Section */}
      <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <div className="inline-block px-6 py-3 mb-6 rounded-full bg-blue-600 text-white font-bold text-lg">
              🏆 Real Projects • Real Results • Real Customers
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
              See Our Professional Snake & Auger Services
            </h2>
            <p className="text-xl text-blue-100 mb-4">
              Colorado First Plumbing snake services across Denver Metro • Milwaukee professional equipment • Expert technicians
            </p>
          </div>

          {/* Hero Gallery - Simplified */}
          <div className="relative mb-12 max-w-5xl mx-auto">
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl border-4 border-trust-primary/20">
              <img
                src="/colorado-plumbing-uploads/existing-projects/sewer-repair-experts.png"
                alt="Professional sewer snake equipment - Colorado First Plumbing"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/80 via-transparent to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <h3 className="text-2xl font-bold mb-2">Professional Sewer Snake Equipment</h3>
                <p className="text-lg text-trust-light">Colorado First Plumbing specialists with Milwaukee professional equipment</p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 shadow-xl border border-blue-500/20 max-w-4xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">
                Ready for Professional Snake Service?
              </h3>
              <p className="text-lg mb-6 text-blue-100">
                See exact snake/auger costs below • Professional clearing guaranteed • Milwaukee equipment
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <a
                  href="#snake-auger-pricing-details"
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-lg font-semibold inline-block transition-all duration-300"
                >
                  💰 See Pricing Below
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Detailed Pricing Section */}
      <section id="snake-auger-pricing-details" className="py-20 bg-neutral-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-authority-navy">
              Transparent Snake & Auger Pricing
            </h2>
            <p className="text-xl text-neutral-gray mb-4">
              See actual snake and auger costs with no hidden fees or surprise charges
            </p>
            <div className="inline-flex items-center bg-success-light px-6 py-3 rounded-full border-2 border-success-primary/20">
              <Shield className="w-6 h-6 text-success-primary mr-3" />
              <span className="text-success-primary font-bold">20+ Snake Services • All Pricing Online</span>
            </div>
          </div>

          {/* Important Service Notes Section */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-8 border-2 border-yellow-300/40 shadow-lg">
              <div className="text-center mb-8">
                <div className="inline-block px-6 py-3 mb-4 rounded-full bg-yellow-400 text-yellow-900 font-bold text-lg">
                  ⚠️ Important Service Information
                </div>
                <h3 className="text-3xl font-bold text-authority-navy mb-4">
                  What Snaking Services Include vs. Specialized Cleaning
                </h3>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* What Snaking DOES Include */}
                <div className="bg-white rounded-xl shadow-lg border border-green-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-success-primary to-success-eco text-white p-4">
                    <h4 className="text-xl font-bold text-center flex items-center justify-center">
                      <CheckCircle className="mr-2" size={24} />
                      What Snaking Services DO Include
                    </h4>
                  </div>
                  <div className="p-6">
                    <ul className="space-y-3 text-neutral-gray">
                      <li className="flex items-start">
                        <CheckCircle size={20} className="text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                        <span><strong>Clear blockages</strong> to restore drainage flow</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle size={20} className="text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                        <span><strong>Get your sewer moving</strong> and working again</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle size={20} className="text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                        <span><strong>Remove basic obstructions</strong> like toilet paper, hair, food</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle size={20} className="text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                        <span><strong>Professional equipment</strong> for effective clearing</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle size={20} className="text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                        <span><strong>Immediate drainage restoration</strong> for emergency situations</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* What Snaking DOES NOT Include */}
                <div className="bg-white rounded-xl shadow-lg border border-red-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-4">
                    <h4 className="text-xl font-bold text-center flex items-center justify-center">
                      <svg className="mr-2" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        <path d="M0 0h24v24H0z" fill="none"/>
                      </svg>
                      What Snaking Services DO NOT Include
                    </h4>
                  </div>
                  <div className="p-6">
                    <ul className="space-y-3 text-neutral-gray">
                      <li className="flex items-start">
                        <svg className="mr-3 mt-0.5 flex-shrink-0" width="20" height="20" fill="#dc2626" viewBox="0 0 24 24">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                        <span><strong>Root Cleaning</strong> - Requires specialized root cutting equipment</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="mr-3 mt-0.5 flex-shrink-0" width="20" height="20" fill="#dc2626" viewBox="0 0 24 24">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                        <span><strong>Hydro Jetting</strong> - High-pressure water cleaning for thorough pipe cleaning</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="mr-3 mt-0.5 flex-shrink-0" width="20" height="20" fill="#dc2626" viewBox="0 0 24 24">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                        <span><strong>Cast Iron Descaling</strong> - Specialized cleaning for old cast iron buildup</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="mr-3 mt-0.5 flex-shrink-0" width="20" height="20" fill="#dc2626" viewBox="0 0 24 24">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                        <span><strong>Grease/Scale Removal</strong> - Requires high-pressure equipment for thorough cleaning</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="mr-3 mt-0.5 flex-shrink-0" width="20" height="20" fill="#dc2626" viewBox="0 0 24 24">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                        <span><strong>Pipe Restoration</strong> - Complete cleaning to restore pipe diameter</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Specialized Service Buttons */}
              <div className="text-center">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-neutral-border">
                  <h4 className="text-2xl font-bold text-authority-navy mb-4">
                    Need Specialized Cleaning Services?
                  </h4>
                  <p className="text-lg text-neutral-gray mb-6">
                    For roots, grease buildup, or thorough pipe cleaning, see our specialized services:
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <a 
                      href="/root-removal-pricing" 
                      className="bg-gradient-to-r from-green-600 to-green-700 hover:shadow-2xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-lg font-bold inline-block transition-all duration-300 w-full sm:w-auto ring-4 ring-green-500/30"
                    >
                      🌿 Root Removal Pricing
                    </a>
                    <a 
                      href="/hydro-jetting-pricing" 
                      className="bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-lg font-bold inline-block transition-all duration-300 w-full sm:w-auto ring-4 ring-blue-500/30"
                    >
                      💨 Hydro Jetting Pricing
                    </a>
                  </div>
                  <div className="mt-4 text-sm text-neutral-gray">
                    <p>Our specialists will help determine if snaking or specialized cleaning is right for your situation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Toilet Snake Services */}
          <div className="mb-12">
            <div className="bg-gradient-to-r from-trust-light to-success-light/30 rounded-2xl p-8 border-2 border-trust-primary/20">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Wrench size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Toilet Snake Services</h3>
                  <p className="text-lg text-neutral-gray">Professional toilet auger service for stubborn clogs</p>
                </div>
              </div>
              <div className="bg-neutral-white rounded-xl shadow-lg border border-trust-primary/10 overflow-hidden">
                <div className="bg-gradient-to-r from-trust-primary to-trust-deep text-white p-4">
                  <h4 className="text-xl font-bold text-center">Toilet Snake Pricing</h4>
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                    <span className="font-semibold text-authority-navy">Toilet Auger Service for Clogged Toilet</span>
                    <span className="text-xl font-bold text-success-primary">$225</span>
                  </div>
                </div>
                <div className="bg-success-light p-4">
                  <h5 className="font-bold text-success-primary mb-3">What's Included</h5>
                  <ul className="space-y-2 text-sm text-success-primary">
                    <li className="flex items-center">
                      <CheckCircle size={16} className="mr-2" />
                      Professional toilet auger equipment
                    </li>
                    <li className="flex items-center">
                      <CheckCircle size={16} className="mr-2" />
                      Testing flush mechanism after service
                    </li>
                    <li className="flex items-center">
                      <CheckCircle size={16} className="mr-2" />
                      Removal of foreign objects when possible
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          {/* Mainline Snake Services */}
          <div className="mb-12">
            <div className="bg-gradient-to-r from-action-primary/10 to-action-urgent/10 rounded-2xl p-8 border-2 border-action-primary/20">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Zap size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Mainline Snake Services</h3>
                  <p className="text-lg text-neutral-gray">Professional main sewer line snake clearing services</p>
                </div>
              </div>
              <div className="bg-neutral-white rounded-xl shadow-lg border border-action-primary/10 overflow-hidden">
                <div className="bg-gradient-to-r from-action-primary to-action-urgent text-white p-4">
                  <h4 className="text-xl font-bold text-center">Mainline Snake Pricing</h4>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                    <span className="font-semibold text-authority-navy">Via Exterior PVC or ABS Double Cleanout (Yard)</span>
                    <span className="text-xl font-bold text-success-primary">$295</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                    <span className="font-semibold text-authority-navy">Via Basement Stack PVC or ABS Cleanout</span>
                    <span className="text-xl font-bold text-success-primary">$295</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                    <span className="font-semibold text-authority-navy">Via Pulled Toilet (Includes Reset)</span>
                    <span className="text-xl font-bold text-success-primary">$695</span>
                  </div>
                </div>
                <div className="bg-success-light p-4">
                  <h5 className="font-bold text-success-primary mb-3">Mainline Snake Advantages</h5>
                  <ul className="space-y-2 text-sm text-success-primary">
                    <li className="flex items-center">
                      <CheckCircle size={16} className="mr-2" />
                      Clears main sewer line blockages
                    </li>
                    <li className="flex items-center">
                      <CheckCircle size={16} className="mr-2" />
                      Restores drainage for entire house
                    </li>
                    <li className="flex items-center">
                      <CheckCircle size={16} className="mr-2" />
                      Professional grade snake equipment
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          {/* Branch Line Snake Services */}
          <div className="mb-12">
            <div className="bg-gradient-to-r from-success-light to-success-primary/10 rounded-2xl p-8 border-2 border-success-primary/20">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Camera size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Branch Line Snake Services</h3>
                  <p className="text-lg text-neutral-gray">Professional branch drain line snake clearing</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-neutral-white rounded-xl shadow-lg border border-success-primary/10 overflow-hidden">
                  <div className="bg-gradient-to-r from-success-primary to-success-eco text-white p-4">
                    <h4 className="text-xl font-bold text-center">Bathroom Snake Services</h4>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                      <span className="font-semibold text-authority-navy">Bathroom Sink Snake</span>
                      <span className="text-xl font-bold text-success-primary">$225</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                      <span className="font-semibold text-authority-navy">Shower Drain Snake</span>
                      <span className="text-xl font-bold text-success-primary">$225</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                      <span className="font-semibold text-authority-navy">Bathtub Drain Snake</span>
                      <span className="text-xl font-bold text-success-primary">$225</span>
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-white rounded-xl shadow-lg border border-success-primary/10 overflow-hidden">
                  <div className="bg-gradient-to-r from-success-primary to-success-eco text-white p-4">
                    <h4 className="text-xl font-bold text-center">Kitchen & Utility Snake</h4>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                      <span className="font-semibold text-authority-navy">Kitchen Sink Snake</span>
                      <span className="text-xl font-bold text-success-primary">$275</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                      <span className="font-semibold text-authority-navy">Laundry/Washing Machine</span>
                      <span className="text-xl font-bold text-success-primary">$275</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                      <span className="font-semibold text-authority-navy">Utility Sink Snake</span>
                      <span className="text-xl font-bold text-success-primary">$275</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Authority Section */}
      <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
            Why Denver Metro Trusts Our Affordable Drain Snake Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
              <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
              <p className="text-blue-100 text-sm">Since 2018 serving Denver Metro with professional drain snake services</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">360</div>
              <div className="text-lg font-semibold text-white mb-2">Google Reviews</div>
              <p className="text-blue-100 text-sm">4.8★ rating</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
              <div className="text-lg font-semibold text-white mb-2">CO Jurisdictions</div>
              <p className="text-blue-100 text-sm">Licensed in 30+ Colorado cities including Denver Metro, with all permits and inspections handled</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">A+</div>
              <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
              <p className="text-blue-100 text-sm">Accredited 7/15/2024</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
              <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
              <p className="text-blue-100 text-sm">Showing Affordable Prices Online with 360 Google Reviews</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gradient-to-br from-neutral-white to-blue-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-5xl font-bold mb-12 text-authority-navy text-center">
            Frequently Asked Questions About Drain Snake Services
          </h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md border border-blue-100">
              <h3 className="font-bold text-lg text-authority-navy mb-3">How much does it cost to snake a drain in Denver?</h3>
              <p className="text-neutral-gray">Colorado First Plumbing offers exact, flat-rate pricing for drain snaking in Denver. A toilet or branch line (bathroom sink, shower) costs $225. A kitchen or laundry line is $275. A mainline snake via a cleanout is $295. We show all our prices online.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-blue-100">
              <h3 className="font-bold text-lg text-authority-navy mb-3">What is the difference between snaking and hydro jetting?</h3>
              <p className="text-neutral-gray">Snaking (or using an auger) is a mechanical process that breaks up a clog to restore flow. It's great for immediate blockages. Hydro jetting is a high-pressure water cleaning service that scours the entire pipe wall to remove grease, scale, and roots for a more thorough, long-lasting clean.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-blue-100">
              <h3 className="font-bold text-lg text-authority-navy mb-3">Will a drain snake remove tree roots?</h3>
              <p className="text-neutral-gray">A standard drain snake is not designed to effectively remove tree roots. While it may punch a temporary hole through them, the roots will quickly grow back. For proper root removal, specialized root-cutting equipment or hydro jetting is required.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-blue-100">
              <h3 className="font-bold text-lg text-authority-navy mb-3">What is the price for a mainline snake if I don't have a cleanout?</h3>
              <p className="text-neutral-gray">If there is no accessible cleanout for the main sewer line, we can access the pipe by removing a toilet. Our flat-rate price for this service is $695. This includes the cost of the mainline snaking plus the labor to pull and professionally reset the toilet with a new seal.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Pricing Section - Continued */}
      <section className="py-20 bg-neutral-white">
        <div className="container mx-auto px-4">
          {/* Final CTA Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-blue-800 to-blue-900 rounded-2xl p-8 text-white shadow-2xl border border-blue-600/20">
              <h3 className="text-3xl font-bold mb-4">Ready for Professional Snake Service?</h3>
              <p className="text-xl mb-6 text-blue-100">
                See your exact price above • No hidden fees • Master plumber owned • 360 Google Reviews
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <a 
                  href="/sewer-cleaning-pricing" 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300 w-full sm:w-auto"
                >
                  🔧 See All Sewer Services
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Schema Injection */}
      <SchemaInjector 
        schemas={[completePageSchema]}
        title="Denver Drain Snake Cost: Toilet $225, Mainline $295 | Colorado First Plumbing"
        description="See exact, flat-rate prices for drain snake services in Denver. Toilet/Branch Line: $225. Kitchen Sink: $275. Mainline: $295. 360 Google Reviews. No hidden fees."
        canonical="https://colorado-plumbing.com/sewer-snake-auger-pricing"
        keywords="denver drain snake cost, sewer snake price, toilet snake cost, kitchen sink clog price, mainline snake cost denver"
      />
    </div>
  );
};

export default SewerSnakeAugerPricing;